import { toast } from 'react-toastify';

export interface CSVInserviceRow {
  workshopTypeName?: string;
  sponsor?: string;
  topicNames?: string; // Comma separated topic names
  personnelNames?: string; // Comma separated "FirstName LastName" pairs
  hours?: string;
  date?: string;
  paidDate?: string;
  elaw?: string;
  foundations?: string;
  foundations15H?: string;
  cpr?: string;
  matDate?: string;
  firstAid?: string;
  matApp?: string;
  sHarassmentExp?: string;
  aces?: string;
  paid?: string; // "true" or "false"
}

export interface CSVError {
  row: number;
  field: string;
  error: string;
  originalData: CSVInserviceRow;
}

export const parseCSV = (csvText: string): CSVInserviceRow[] => {
  const lines = csvText.split('\n').filter(line => line.trim());
  if (lines.length < 2) {
    toast.error('CSV must contain at least a header row and one data row');
    return [];
  }

  const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
  const rows: CSVInserviceRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
    
    if (values.length !== headers.length) {
      toast.error(`Row ${i + 1} has incorrect number of columns`);
      continue;
    }

    const row: CSVInserviceRow = {};
    headers.forEach((header, index) => {
      row[header as keyof CSVInserviceRow] = values[index] || '';
    });

    rows.push(row);
  }

  return rows;
};

export const convertCSVToPayload = (csvRows: CSVInserviceRow[], workshops: any[], topics: any[], personnel: any[]) => {
  const payloads: any[] = [];

  csvRows.forEach((row, index) => {
    const currentPayload: any = {};

    // Map workshop type
    if (row.workshopTypeName) {
      const workshop = workshops.find((w: any) => 
        w.workshopTypeName.toLowerCase() === row.workshopTypeName?.toLowerCase()
      );
      if (workshop) {
        currentPayload.workshopTypeId = workshop.workshopTypeID;
      } else {
        toast.error(`Row ${index + 1}: Workshop "${row.workshopTypeName}" not found`);
      }
    }

    // Map sponsor
    if (row.sponsor) {
      currentPayload.sponsor = row.sponsor;
    }

    // Map topics
    if (row.topicNames) {
      const topicNames = row.topicNames.split(';').map(t => t.trim());
      const topicIds: number[] = [];
      
      topicNames.forEach(topicName => {
        const topic = topics.find((t: any) => 
          t.topicName.toLowerCase() === topicName.toLowerCase()
        );
        if (topic) {
          topicIds.push(topic.topicID);
        } else {
          toast.error(`Row ${index + 1}: Topic "${topicName}" not found`);
        }
      });
      
      currentPayload.TopicIds = topicIds; // API expects TopicIds (PascalCase)
    }

    // Map personnel
    if (row.personnelNames) {
      const personnelNames = row.personnelNames.split(';').map(p => p.trim());
      const personnelIds: number[] = [];
      
      personnelNames.forEach(personName => {
        const [firstName, lastName] = personName.split(' ').map(n => n.trim());
        const person = personnel.find((p: any) => 
          (p.firstname?.toLowerCase() === firstName?.toLowerCase() && 
           p.lastname?.toLowerCase() === lastName?.toLowerCase()) ||
          p.firstname?.toLowerCase() === personName.toLowerCase() ||
          p.lastname?.toLowerCase() === personName.toLowerCase()
        );
        if (person) {
          personnelIds.push(person.personalID);
        } else {
          toast.error(`Row ${index + 1}: Personnel "${personName}" not found`);
        }
      });
      
      currentPayload.PersonnelIds = personnelIds; // API expects PersonnelIds (PascalCase)
    }

    // Map other fields
    if (row.hours) {
      const hours = parseFloat(row.hours);
      if (!isNaN(hours)) {
        currentPayload.hours = hours.toFixed(2);
      }
    }

    // Helper function to convert date format
    const convertDateFormat = (dateStr: string): string => {
      if (!dateStr) return '';
      
      // Try to parse YYYY/MM/DD format
      const slashParts = dateStr.split('/');
      if (slashParts.length === 3) {
        const year = slashParts[0];
        const month = slashParts[1].padStart(2, '0');
        const day = slashParts[2].padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      
      // Try to parse MM/DD/YYYY format
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
      
      // Try to parse YYYY-MM-DD format (already correct)
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateStr;
      }
      
      // Try to parse as Date object and format
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      
      return dateStr; // Return original if can't parse
    };

    // Map date fields with proper format conversion
    if (row.date) {
      currentPayload.date = convertDateFormat(row.date);
    }

    if (row.paidDate) {
      currentPayload.paidDate = convertDateFormat(row.paidDate);
    }

    if (row.elaw) {
      currentPayload.elaw = convertDateFormat(row.elaw);
    }

    if (row.foundations) {
      currentPayload.foundations = convertDateFormat(row.foundations);
    }

    if (row.foundations15H) {
      currentPayload.foundations15H = convertDateFormat(row.foundations15H);
    }

    if (row.cpr) {
      currentPayload.cpr = convertDateFormat(row.cpr);
    }

    if (row.matDate) {
      currentPayload.matDate = convertDateFormat(row.matDate);
    }

    if (row.firstAid) {
      currentPayload.firstAid = convertDateFormat(row.firstAid);
    }

    if (row.matApp) {
      currentPayload.matApp = convertDateFormat(row.matApp);
    }

    if (row.sHarassmentExp) {
      currentPayload.sHarassmentExp = convertDateFormat(row.sHarassmentExp);
    }

    if (row.aces) {
      currentPayload.aces = convertDateFormat(row.aces);
    }

    if (row.paid) {
      currentPayload.paid = row.paid.toLowerCase() === 'true' || row.paid === '1';
    }

    // Send payload directly (not wrapped in formDto) to match regular form submission
    payloads.push(currentPayload);
  });

  return payloads;
};

export const downloadErrorCSV = (errors: CSVError[], originalData: CSVInserviceRow[]) => {
  const headers = [
    'Row',
    'Error Field',
    'Error Message',
    'Original Data',
    'Workshop Type',
    'Sponsor',
    'Topic Names',
    'Personnel Names',
    'Hours',
    'Date',
    'Paid Date',
    'E. Laws',
    'Foundations',
    'Foundations 15H',
    'CPR',
    'MAT Date',
    'First Aid',
    'MAT App',
    'Sexual Harassment I',
    'ACES',
    'Paid'
  ];

  const errorRows = errors.map(error => {
    const originalRow = error.originalData;
    return [
      error.row,
      error.field,
      error.error,
      JSON.stringify(originalRow),
      originalRow.workshopTypeName || '',
      originalRow.sponsor || '',
      originalRow.topicNames || '',
      originalRow.personnelNames || '',
      originalRow.hours || '',
      originalRow.date || '',
      originalRow.paidDate || '',
      originalRow.elaw || '',
      originalRow.foundations || '',
      originalRow.foundations15H || '',
      originalRow.cpr || '',
      originalRow.matDate || '',
      originalRow.firstAid || '',
      originalRow.matApp || '',
      originalRow.sHarassmentExp || '',
      originalRow.aces || '',
      originalRow.paid || ''
    ].map(field => `"${field}"`);
  });

  const csvContent = headers.join(',') + '\n' + errorRows.map(row => row.join(',')).join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'csv_import_errors.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  
  toast.success('Error CSV downloaded successfully');
};

export const validateCSVRow = (row: CSVInserviceRow, rowIndex: number, workshops: any[], topics: any[], personnel: any[]): CSVError[] => {
  const errors: CSVError[] = [];

  // Validate workshop type
  if (row.workshopTypeName) {
    const workshop = workshops.find((w: any) => 
      w.workshopTypeName.toLowerCase() === row.workshopTypeName?.toLowerCase()
    );
    if (!workshop) {
      errors.push({
        row: rowIndex,
        field: 'workshopTypeName',
        error: `Workshop "${row.workshopTypeName}" not found in system`,
        originalData: row
      });
    }
  }

  // Validate topics
  if (row.topicNames) {
    const topicNames = row.topicNames.split(';').map(t => t.trim());
    topicNames.forEach(topicName => {
      const topic = topics.find((t: any) => 
        t.topicName.toLowerCase() === topicName.toLowerCase()
      );
      if (!topic) {
        errors.push({
          row: rowIndex,
          field: 'topicNames',
          error: `Topic "${topicName}" not found in system`,
          originalData: row
        });
      }
    });
  }

  // Validate personnel
  if (row.personnelNames) {
    const personnelNames = row.personnelNames.split(';').map(p => p.trim());
    personnelNames.forEach(personName => {
      const [firstName, lastName] = personName.split(' ').map(n => n.trim());
      const person = personnel.find((p: any) => 
        (p.firstname?.toLowerCase() === firstName?.toLowerCase() && 
         p.lastname?.toLowerCase() === lastName?.toLowerCase()) ||
        p.firstname?.toLowerCase() === personName.toLowerCase() ||
        p.lastname?.toLowerCase() === personName.toLowerCase()
      );
      if (!person) {
        errors.push({
          row: rowIndex,
          field: 'personnelNames',
          error: `Personnel "${personName}" not found in system`,
          originalData: row
        });
      }
    });
  }

  // Validate hours
  if (row.hours) {
    const hours = parseFloat(row.hours);
    if (isNaN(hours) || hours < 0) {
      errors.push({
        row: rowIndex,
        field: 'hours',
        error: `Invalid hours value: "${row.hours}". Must be a positive number`,
        originalData: row
      });
    }
  }

  // Validate dates - check for YYYY-MM-DD or YYYY/MM/DD format
  const dateFields = ['date', 'paidDate', 'elaw', 'foundations', 'foundations15H', 'cpr', 'matDate', 'firstAid', 'matApp', 'sHarassmentExp', 'aces'];
  dateFields.forEach(field => {
    const value = row[field as keyof CSVInserviceRow];
    if (value && value.trim()) {
      // Check for YYYY-MM-DD format
      if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Valid format, also check if it's a valid date
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          errors.push({
            row: rowIndex,
            field: field,
            error: `Invalid date: "${value}". Date is not valid`,
            originalData: row
          });
        }
      }
      // Check for YYYY/MM/DD format
      else if (value.match(/^\d{4}\/\d{2}\/\d{2}$/)) {
        // Valid format, also check if it's a valid date
        const date = new Date(value.replace(/\//g, '-'));
        if (isNaN(date.getTime())) {
          errors.push({
            row: rowIndex,
            field: field,
            error: `Invalid date: "${value}". Date is not valid`,
            originalData: row
          });
        }
      }
      // Check for MM/DD/YYYY format
      else if (value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        // Valid format, also check if it's a valid date
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          errors.push({
            row: rowIndex,
            field: field,
            error: `Invalid date: "${value}". Date is not valid`,
            originalData: row
          });
        }
      }
      else {
        errors.push({
          row: rowIndex,
          field: field,
          error: `Invalid date format: "${value}". Use YYYY-MM-DD, YYYY/MM/DD, or MM/DD/YYYY format (e.g., 2024-01-15, 2024/01/15, or 01/15/2024)`,
          originalData: row
        });
      }
    }
  });

  // Validate paid field
  if (row.paid && row.paid.trim()) {
    const paidValue = row.paid.toLowerCase();
    if (paidValue !== 'true' && paidValue !== 'false' && paidValue !== '1' && paidValue !== '0') {
      errors.push({
        row: rowIndex,
        field: 'paid',
        error: `Invalid paid value: "${row.paid}". Must be true/false or 1/0`,
        originalData: row
      });
    }
  }

  return errors;
};

export const downloadCSVTemplate = () => {
  const headers = [
    'workshopTypeName',
    'sponsor', 
    'topicNames',
    'personnelNames',
    'hours',
    'date',
    'paidDate',
    'elaw',
    'foundations',
    'foundations15H',
    'cpr',
    'matDate',
    'firstAid',
    'matApp',
    'sHarassmentExp',
    'aces',
    'paid'
  ];

  const sampleData = [
    'Sample Workshop',
    'Sample Sponsor',
    'Topic1;Topic2',
    'John Doe;Jane Smith',
    '2.50',
    '2024-01-15',
    '2024-01-20',
    '2024-01-10',
    '2024-01-12',
    '2024-01-14',
    '2024-01-16',
    '2024-01-18',
    '2024-01-22',
    '2024-01-24',
    '2024-01-26',
    '2024-01-28',
    'true'
  ];

  const csvContent = headers.join(',') + '\n' + sampleData.join(',');
  
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'inservice_template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  
  toast.success('CSV template downloaded successfully');
};
